import * as React from 'react';
import { connect } from 'react-redux';
import { push, goBack } from 'react-router-redux';
import * as moment from 'moment-business-days';
import {
  Card,
  Icon,
  IconButton,
  Notification,
  BookingPayment,
  BookingPrices,
  HeadingSection,
  RouteWrapper,
  Section,
} from '../../components';
import EXPIRATIONS from '../../data/expirations';
import { resolveBooking, getBooking } from '../../redux/actions/bookings';
import { getVehicleType } from '../../redux/actions/vehicleTypes';

interface Props {
  match: any;
  dispatch: Function;
}

interface State {
  id: number;
  isReady: boolean;
  error: string | boolean;
  isPaid: boolean;
  isLoading: boolean;
  booking?: any;
  vehicleType?: any;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      id: Number(this.props.match.params.id),
      isReady: false,
      isLoading: true,
      error: false,
      isPaid: false,
    };
    this.handleUpdate = this.handleUpdate.bind(this);
  }

  /**
   * Retrieve all bookings and vehicle types from API
   */
  async componentDidMount() {
    const bookingResponse = await this.props.dispatch(
      getBooking(this.state.id),
    );
    if (bookingResponse.error) {
      this.setState({ error: bookingResponse.error });
      return;
    }
    if (bookingResponse.data.status === 'CANCELLED') {
      this.setState({ error: 'This booking has been cancelled' });
      return;
    }
    this.handleUpdate(bookingResponse);
  }

  /**
   * Handle a booking update
   */
  async handleUpdate(response, vehicleType = this.state.vehicleType) {
    const updateState: any = {
      booking: response.data,
      isPaid:
        response.data.payment_status > 0 && response.data.payment_status < 4,
      isLoading: false,
      isReady: true,
    };
    if (!updateState.isPaid && response.data.payment_status !== 4) {
      this.setState({
        error: 'This booking has not been paid for via Pre-Auth payment',
      });
      return;
    }
    if (vehicleType === undefined) {
      const vehicleTypeResponse = await this.props.dispatch(
        getVehicleType(response.data.vehicle_type_id),
      );
      if (vehicleTypeResponse.error) {
        this.setState({ error: vehicleTypeResponse.error });
        return;
      }
      updateState.vehicleType = vehicleTypeResponse.data;
    }

    this.setState(updateState);
  }

  /**
   * Attempt to resolve a booking after it has been paid for and is ON-HOLD
   */
  private async attemptToResolve(response: any): Promise<boolean> {
    const {
      data: { id, status, payment_status },
    } = response;
    if (status === 'ON-HOLD' && payment_status === 1) {
      this.setState({ isLoading: true });
      await this.props.dispatch(resolveBooking(id));
      this.props.dispatch(push('/'));
      this.setState({ isLoading: false });
      return true;
    }
    return false;
  }

  render() {
    const { booking, vehicleType } = this.state;
    let daysAgo;
    let expired;
    if (booking !== undefined) {
      daysAgo = moment().businessDiff(moment(booking.pre_auth_expiry_date)) - 1;
      expired = daysAgo > EXPIRATIONS.PRE_AUTH;
    }
    return (
      <RouteWrapper
        title="Resolve Pre-Auth"
        ready={this.state.isReady}
        error={this.state.error}
        loading={this.state.isLoading}
        breadcrumbs={[
          {
            link: '/payments',
            text: 'Payments',
          },
          {
            link: '/payments/pre-auth',
            text: 'Pre-Auth Bookings',
          },
          {
            link: `/payments/pre-auth/${this.state.id}`,
            text: 'Resolve',
          },
        ]}
      >
        <HeadingSection
          title={`Resolve Pre-Auth for ${(this.state.booking &&
            this.state.booking.contact_name) ||
            'N/A'}`}
          controls={[
            <IconButton
              key="booking-link"
              icon="truck"
              className="is-default"
              handleClick={() => {
                this.props.dispatch(push(`/bookings/${this.state.id}`));
              }}
            >
              View Booking
            </IconButton>,
            <IconButton
              key="back-button"
              icon="undo"
              className="is-default"
              handleClick={() => {
                this.props.dispatch(goBack());
              }}
            >
              Return
            </IconButton>,
          ]}
        />
        <Section>
          <div className="columns is-centered">
            <div className="column is-12-desktop is-7-widescreen">
              <Notification type={expired ? 'danger' : 'default'}>
                This booking was paid for via Pre-Auth payment, which
                {expired ? ' has expired by ' : ' is due to expire in '}
                {Math.abs(daysAgo - EXPIRATIONS.PRE_AUTH)} working day(s)
              </Notification>
            </div>
          </div>
          <div className="columns is-centered">
            <div className="column is-12-desktop is-7-widescreen">
              {!this.state.isPaid && (
                <Card>
                  <div className="field">
                    <BookingPrices
                      booking={booking}
                      vehicleType={vehicleType}
                    />
                  </div>
                  <div className="field">
                    <BookingPayment
                      booking={booking}
                      handleUpdate={(response) => {
                        this.setState({ isLoading: true }, async () => {
                          if (!(await this.attemptToResolve(response))) {
                            await this.handleUpdate(response);
                            this.props.dispatch(goBack());
                          }
                        });
                      }}
                    />
                  </div>
                </Card>
              )}
              {this.state.isPaid && (
                <Notification type="info">
                  <Icon icon="check-circle" />
                  &nbsp; This booking's payment has been resolved
                </Notification>
              )}
            </div>
          </div>
        </Section>
      </RouteWrapper>
    );
  }
}

export default connect()(Route);
